import { AnimatePresence, motion } from 'framer-motion';
import { Bell, CheckCircle2, X, XCircle } from 'lucide-react';
import type { AppNotification } from '../../services/api';

interface ContributionNotificationToastProps {
  notification: AppNotification | null;
  onDismiss: () => void;
  onOpen: () => void;
}

export function ContributionNotificationToast({
  notification,
  onDismiss,
  onOpen,
}: ContributionNotificationToastProps) {
  const isApproved = notification?.title.toLowerCase().includes('approved');

  return (
    <AnimatePresence>
      {notification ? (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ type: 'spring', damping: 26, stiffness: 340 }}
          className="fixed left-4 right-4 z-[65] mx-auto max-w-md pointer-events-auto"
          style={{ bottom: 'calc(2vh + 5.5rem)' }}
          role="status"
          aria-live="polite"
        >
          <div className="rounded-2xl border border-slate-200/90 bg-white/95 p-4 shadow-xl shadow-slate-900/10 backdrop-blur-xl dark:border-gray-600/70 dark:bg-gray-900/95 dark:shadow-black/40">
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                  isApproved
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                }`}
              >
                {isApproved ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900 dark:text-gray-50">{notification.title}</p>
                  <button
                    type="button"
                    onClick={onDismiss}
                    className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    aria-label="Dismiss notification"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-gray-300">{notification.message}</p>
                <button
                  type="button"
                  onClick={onOpen}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500"
                >
                  <Bell className="h-3.5 w-3.5" />
                  View submission
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
